import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import ClearIcon from '@mui/icons-material/Clear';
import { runJoiValidate } from "src/_utils";
import { cloneDeep, omit, omitBy } from "lodash";
import OrderBasicInfoCard from "./OrderBasicInfoCard";
import OrderItemsCard from "./OrderItemsCard";
import ContactDetails from "src/_modules/common/Details/ContactDetailsCard";
import { AddressDetailsCard, MapLocationCard, ConfirmButtonDialog } from "src/_modules/common";
import { useSelector } from "react-redux";
import QuickShippingCard from "./QuickShippingCard";
import { ROLES } from "src/constants";

export default function OrderManagementDialog({
    order,
    openDialog,
    loading,
    isEditing,
    handleOrderChange,
    onCreate,
    onUpdate,
    onDelete,
    onClose,
    schema
}) {
    const { t } = useTranslation()
    const { profile } = useSelector((state) => state.entities.profile)
    const dialogRef = useRef()
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [validationErrors, setValidationsErrors] = useState(null);

    const onCloseHandler = () => {
        setValidationsErrors(null)
        onClose()
    }

    const handleSubmit = () => {
        try {
            setValidationsErrors(null)
            let payload = cloneDeep(order)
            if (!!schema) {
                const errors = runJoiValidate(schema, omit(payload, ["id"]))
                console.log("errors")
                console.log(payload)
                console.log(errors)
                payload = omitBy(payload, v => !Boolean(v))
                if (errors) {
                    setValidationsErrors(errors)
                    dialogRef.current.scrollTo({ top: 0, behavior: 'smooth' })
                    return
                }
            }
            if (isEditing) {
                onUpdate(payload)
            } else {
                onCreate(payload)
            }
        } catch (e) {
            console.error("Error while submitting order", e)
        }
    }

    return (
        <Dialog
            maxWidth="md"
            dir={theme.direction}
            fullWidth
            fullScreen={fullScreen}
            open={openDialog}
            aria-labelledby="order-dialog-title"
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {`${isEditing ? t('Edit order') : t('Create order')}`}
                <IconButton onClick={onCloseHandler} color="primary">
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ px: {xs: 0.5 , sm: 3} }} ref={dialogRef}>
                <Grid container spacing={3}>
                    {["FIRST_AND_LAST_MILE"].includes(profile?.merchant?.shippingType)
                        &&
                        <Grid item xs={12}>
                            <QuickShippingCard value={order?.quickShipping} handleChange={handleOrderChange} />
                        </Grid>
                    }
                    {/* Order Info */}
                    <Grid item xs={12}>
                        <OrderBasicInfoCard
                            order={order}
                            handleChange={handleOrderChange}
                            validationErrors={validationErrors}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <OrderItemsCard
                            type={"add"}
                            orderItems={order?.orderItems}
                            validationErrors={validationErrors}
                            handleChange={handleOrderChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h4">{t("User's Info")}</Typography>
                    </Grid>
                    {/* Contact and address */}
                    <Grid item xs={12}>
                        <ContactDetails
                            contact={order?.user}
                            validationErrors={validationErrors?.user}
                            handleChange={({ target: { name, value } }) => {
                                handleOrderChange({ target: { name: `user.${name}`, value } })
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AddressDetailsCard
                            address={order?.user?.address}
                            handleChange={({ target: { name, value } }) => {
                                name = ["address", "location.coordinates"].includes(name) ? `user.${name}` : `user.address.${name}`
                                handleOrderChange({ target: { name: name, value: value } })
                            }}
                            nationalAddressRequired={true}
                            validationErrors={validationErrors?.user?.address}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <MapLocationCard
                            handleChange={({ target: { name, value } }) => handleOrderChange({ target: { name: `user.location.${name}`, value } })}
                            coordinates={order?.user?.location?.coordinates}
                            error={validationErrors?.user?.location?.coordinates}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <Box>
                    <LoadingButton
                        variant="contained"
                        disableElevation
                        onClick={handleSubmit}
                        loading={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                        }}
                    >{isEditing ? t("Edit") : t("Add")}</LoadingButton>
                    {(isEditing && !!onDelete) &&
                        <ConfirmButtonDialog
                            buttonProps={{
                                variant: "contained",
                                color: "error",
                                sx: {
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    mx: 1
                                }
                            }}
                            title={t("Confirm Deletion")}
                            content={t("Are you sure you want to delete it?")}
                            confirmFn={() => onDelete(order.id)}
                        >
                            {t("Delete")}
                        </ConfirmButtonDialog>
                    }
                </Box>
                <Button
                    variant="contained"
                    sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                    }}
                    onClick={onCloseHandler}>
                    {t("Close")}
                </Button>
            </DialogActions>

        </Dialog>
    )
}